import { motion } from 'framer-motion'

interface BmiGaugeProps {
  bmi: number
  animated?: boolean
  size?: 'default' | 'small'
}

const BMI_MIN = 15
const BMI_MAX = 45

export type BmiCategory = 'underweight' | 'healthy' | 'overweight' | 'obesity_1' | 'obesity_2' | 'obesity_3'
export type BmiDisplayCategory = 'underweight' | 'healthy' | 'overweight' | 'higher_weight'

export function getBmiCategory(bmi: number): {
  label: string
  clinicalLabel: string
  color: string
  category: BmiCategory
  displayCategory: BmiDisplayCategory
} {
  if (bmi < 18.5) return { label: 'Underweight', clinicalLabel: 'Underweight', color: '#60a5fa', category: 'underweight', displayCategory: 'underweight' }
  if (bmi < 25) return { label: 'Healthy Range', clinicalLabel: 'Healthy Weight', color: '#4ade80', category: 'healthy', displayCategory: 'healthy' }
  if (bmi < 30) return { label: 'Overweight', clinicalLabel: 'Overweight', color: '#fbbf24', category: 'overweight', displayCategory: 'overweight' }
  if (bmi < 35) return { label: 'Higher Weight', clinicalLabel: 'Obesity Class I', color: '#fb923c', category: 'obesity_1', displayCategory: 'higher_weight' }
  if (bmi < 40) return { label: 'Higher Weight', clinicalLabel: 'Obesity Class II', color: '#f87171', category: 'obesity_2', displayCategory: 'higher_weight' }
  return { label: 'Higher Weight', clinicalLabel: 'Obesity Class III', color: '#ef4444', category: 'obesity_3', displayCategory: 'higher_weight' }
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max)
}

export function calculateBmi(heightFeet: number, heightInches: number, weightLbs: number): number {
  const totalInches = heightFeet * 12 + heightInches
  if (totalInches <= 0 || weightLbs <= 0) return 0
  return (weightLbs / (totalInches * totalInches)) * 703
}

export function BmiGauge({ bmi, animated = true, size = 'default' }: BmiGaugeProps) {
  const clampedBmi = clamp(bmi, BMI_MIN, BMI_MAX)
  const percentage = ((clampedBmi - BMI_MIN) / (BMI_MAX - BMI_MIN)) * 100
  const angle = (percentage / 100) * 180 - 90
  const category = getBmiCategory(bmi)
  const displayBmi = bmi > 0 ? bmi.toFixed(1) : '—'
  const isSmall = size === 'small'
  const cx = 140
  const cy = 110
  const radius = isSmall ? 60 : 85
  const strokeWidth = isSmall ? 16 : 22

  return (
    <div className={`mx-auto w-full ${isSmall ? 'max-w-[200px]' : 'max-w-sm'}`}>
      <svg viewBox="0 0 280 150" className="w-full" style={{ overflow: 'visible' }}>
        <defs>
          <linearGradient id="bmiGaugeGradient" x1="0%" y1="50%" x2="100%" y2="50%">
            <stop offset="0%" stopColor="#60a5fa" />
            <stop offset="28%" stopColor="#4ade80" />
            <stop offset="50%" stopColor="#fbbf24" />
            <stop offset="75%" stopColor="#fb923c" />
            <stop offset="100%" stopColor="#f87171" />
          </linearGradient>
          <filter id="glow" x="-20%" y="-20%" width="140%" height="140%">
            <feGaussianBlur stdDeviation="2" result="coloredBlur" />
            <feMerge>
              <feMergeNode in="coloredBlur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
          <filter id="needleShadow" x="-50%" y="-50%" width="200%" height="200%">
            <feDropShadow dx="0" dy="2" stdDeviation="3" floodOpacity="0.3" />
          </filter>
        </defs>

        <path d={`M ${cx - radius} ${cy} A ${radius} ${radius} 0 0 1 ${cx + radius} ${cy}`} fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth={strokeWidth + 4} strokeLinecap="round" />
        <path d={`M ${cx - radius} ${cy} A ${radius} ${radius} 0 0 1 ${cx + radius} ${cy}`} fill="none" stroke="url(#bmiGaugeGradient)" strokeWidth={strokeWidth} strokeLinecap="round" filter="url(#glow)" />

        {!isSmall &&
          [18.5, 25, 30, 35].map((value, i) => {
            const tickPercentage = ((value - BMI_MIN) / (BMI_MAX - BMI_MIN)) * 100
            const tickAngle = (tickPercentage / 100) * 180
            const radians = (tickAngle - 180) * (Math.PI / 180)
            const innerRadius = radius + strokeWidth / 2 + 4
            const outerRadius = radius + strokeWidth / 2 + 12
            const x1 = cx + innerRadius * Math.cos(radians)
            const y1 = cy + innerRadius * Math.sin(radians)
            const x2 = cx + outerRadius * Math.cos(radians)
            const y2 = cy + outerRadius * Math.sin(radians)
            const labelRadius = radius + strokeWidth / 2 + 24
            const labelX = cx + labelRadius * Math.cos(radians)
            const labelY = cy + labelRadius * Math.sin(radians)
            return (
              <g key={i}>
                <line x1={x1} y1={y1} x2={x2} y2={y2} stroke="rgba(255,255,255,0.3)" strokeWidth="2" strokeLinecap="round" />
                <text x={labelX} y={labelY} fill="rgba(255,255,255,0.5)" fontSize="11" fontWeight="600" textAnchor="middle" dominantBaseline="middle">
                  {value}
                </text>
              </g>
            )
          })}

        <motion.g
          initial={animated ? { rotate: -90 } : { rotate: angle }}
          animate={{ rotate: angle }}
          transition={{ duration: 1, ease: [0.34, 1.56, 0.64, 1] }}
          style={{ transformOrigin: `${cx}px ${cy}px` }}
          filter="url(#needleShadow)"
        >
          <polygon
            points={`${cx - 4},${cy} ${cx + 4},${cy} ${cx + 2},${cy - radius + 20} ${cx},${cy - radius + 12} ${cx - 2},${cy - radius + 20}`}
            fill={bmi > 0 ? category.color : 'rgba(255,255,255,0.3)'}
          />
          <circle cx={cx} cy={cy} r={isSmall ? 12 : 16} fill={bmi > 0 ? category.color : 'rgba(255,255,255,0.2)'} />
          <circle cx={cx} cy={cy} r={isSmall ? 6 : 8} fill="#1a1f1a" />
          <circle cx={cx - 2} cy={cy - 2} r={isSmall ? 2 : 3} fill="rgba(255,255,255,0.3)" />
        </motion.g>
      </svg>

      <div className="-mt-4 text-center">
        <p className="mb-1 text-[10px] font-medium uppercase tracking-[0.2em] text-white/40">Your BMI</p>
        <motion.div key={displayBmi} initial={animated ? { scale: 0.8, opacity: 0 } : {}} animate={{ scale: 1, opacity: 1 }} className="relative inline-block">
          <span className={`${isSmall ? 'text-4xl' : 'text-5xl'} font-bold leading-none`} style={{ color: bmi > 0 ? category.color : 'rgba(255,255,255,0.3)' }}>
            {displayBmi}
          </span>
        </motion.div>
        {bmi > 0 && (
          <motion.div initial={animated ? { opacity: 0, y: 5 } : {}} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }} className="mt-2">
            <span className={`inline-block rounded-full px-3 py-1 ${isSmall ? 'text-xs' : 'text-sm'} font-semibold`} style={{ color: category.color, backgroundColor: `${category.color}15` }}>
              {category.label}
            </span>
          </motion.div>
        )}
      </div>
    </div>
  )
}

export function BmiRangeTable({ currentBmi }: { currentBmi?: number }) {
  const ranges = [
    { label: 'Underweight', range: '< 18.5', color: '#60a5fa', min: 0, max: 18.5 },
    { label: 'Healthy', range: '18.5 – 24.9', color: '#4ade80', min: 18.5, max: 25 },
    { label: 'Overweight', range: '25 – 29.9', color: '#fbbf24', min: 25, max: 30 },
    { label: 'Higher Weight', range: '30+', color: '#fb923c', min: 30, max: Infinity },
  ]

  return (
    <div className="mt-5 grid grid-cols-2 gap-2">
      {ranges.map((item) => {
        const isCurrentRange = currentBmi !== undefined && currentBmi >= item.min && currentBmi < item.max
        return (
          <div
            key={item.label}
            className={`relative flex items-center gap-2.5 rounded-xl px-3 py-2.5 transition-all ${isCurrentRange ? 'bg-white/10' : 'bg-white/[0.03]'}`}
            style={isCurrentRange ? { boxShadow: `inset 0 0 0 1px ${item.color}40` } : {}}
          >
            <div className="h-2.5 w-2.5 flex-shrink-0 rounded-full" style={{ backgroundColor: item.color, boxShadow: isCurrentRange ? `0 0 0 2px #1a1f1a, 0 0 0 4px ${item.color}` : 'none' }} />
            <div className="min-w-0 flex-1">
              <span className={`block text-sm leading-tight ${isCurrentRange ? 'font-semibold text-white' : 'text-white/60'}`}>{item.label}</span>
              <span className="mt-0.5 block text-[10px] text-white/40">{item.range}</span>
            </div>
            {isCurrentRange && (
              <div className="absolute -right-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full text-[8px] font-bold" style={{ backgroundColor: item.color, color: '#1a1f1a' }}>
                ✓
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}
